<template>
    <v-flex>
        <v-progress-linear
            v-if="overlay && ! (!! user)"
            absolute
            indeterminate
        ></v-progress-linear>

        <card-collection
            :class="{ 'px-0-nested': (!! user) }"
            :meta="meta"
            :busy="overlay"
            @updated="updated"
        >
            <template #filters>
                <v-toolbar
                    v-if="! (!! user)"
                    elevation="0"
                    color="transparent"
                    class="mt-7"
                    height="56"
                >
                    <v-text-field
                        v-model="queries.search"
                        @click:clear="clearSearch"
                        class="input-search md mr-2"
                        clearable
                        outlined
                        dense
                        hide-details
                        prepend-inner-icon="mdi-magnify"
                        :label="t('general_label_search')"
                    ></v-text-field>

                    <v-select
                        v-model="queries.statuses"
                        :items="statusItems"
                        class="md mr-2"
                        outlined
                        dense
                        append-icon="mdi-chevron-down"
                        hide-details
                        :label="t('main_goal_label_status')"
                    ></v-select>

                    <v-select
                        v-model="queries.type"
                        :items="typeItems"
                        class="md mr-2"
                        outlined
                        dense
                        append-icon="mdi-chevron-down"
                        hide-details
                        :label="t('main_goal_label_type')"
                    ></v-select>

                    <v-btn
                        @click="capture"
                        class="sm mx-5"
                        color="primary"
                    >{{ t('general_action_search') }}</v-btn>

                    <v-spacer></v-spacer>

                    <v-btn
                        v-ripple="false"
                        plain
                        color="orange darken-4"
                        @click="reset"
                    >{{ t('general_action_reset_filters') }}</v-btn>
                </v-toolbar>
            </template>

            <template #data>
                <thead>
                <tr>
                    <th>{{ t('main_goal_label_id') }}</th>
                    <th v-if="! (!! user)">{{ t('premium_label_user_id') }}</th>
                    <th>{{ t('main_goal_label_title') }}</th>
                    <th>{{ t('main_goal_label_type') }}</th>
                    <th>{{ t('main_goal_label_plan_type') }}</th>
                    <th>{{ t('main_goal_label_status') }}</th>
                    <th>{{ t('main_goal_label_start_at') }}</th>
                    <th>{{ t('main_goal_label_end_at') }}</th>
                    <th>{{ t('main_goal_label_duration') }}</th>
                    <th>{{ t('main_goal_label_program') }}</th>
                    <th v-if="!! user"></th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="collection.data.length === 0">
                    <td class="text-center" colspan="11">{{ t('general_text_no_data') }}</td>
                </tr>
                <template v-else v-for="goal in collection.data">
                    <tr>
                        <td>
                            <a href="javascript:" @click.prevent="touch(goal)">{{ goal.id }}</a>
                        </td>
                        <td v-if="! (!! user)">
                            <router-link :to="{ name: 'trainees.show', params: { id: goal.userId }, query: { tab: '2' } }">
                                {{ goal.userId }}
                            </router-link>
                        </td>
                        <td>
                            <a href="javascript:" @click.prevent="touch(goal)">{{ goal.title }}</a>
                        </td>
                        <td>{{ mainGoalTypeTitle(goal.type) }}</td>
                        <td>{{ goal.planType ? planTypeTitle(goal.planType) : '-' }}</td>
                        <td>
                            <v-chip
                                small
                                :color="statusColor(goal.status)"
                                text-color="white"
                            >{{ mainGoalStatusTitle(goal.status) }}</v-chip>
                        </td>
                        <td>{{ goal.startAt ? goal.startAt.format('DD/MM/YYYY') : '-' }}</td>
                        <td>{{ goal.endAt ? goal.endAt.format('DD/MM/YYYY') : '-' }}</td>
                        <td>{{ goal.durationFormatted || '-' }}</td>
                        <td>{{ goal.program ? goal.program.name : '-' }}</td>
                        <td v-if="!! user" class="text-right">
                            <confirm-modal
                                :title="t('general_title_confirm_action')"
                                @agree="del(goal)"
                            >
                                <template #default="{ on, attrs }">
                                    <v-btn
                                        icon
                                        small
                                        color="orange"
                                        v-on="on"
                                        v-bind="attrs"
                                    ><v-icon small>mdi-delete-outline</v-icon></v-btn>
                                </template>
                            </confirm-modal>
                        </td>
                    </tr>
                </template>
                </tbody>
            </template>
        </card-collection>
    </v-flex>
</template>

<script lang="ts">
    import { defineComponent, reactive, computed, onMounted, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useEnums } from '@/core/defaults'
    import { UserMainGoalCollection } from '@/store/modules/user-main-goal-collection'
    import { MainGoalCollection } from '@/store/modules/main-goal-collection'
    import { UserMainGoal } from '@/core/models/user-main-goal'
    import { User } from '@/core/models/user'
    import CardCollection from '@/components/CardCollection.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'

    export default defineComponent({
        name: 'UserMainGoalCollection',

        components: {
            CardCollection,
            ConfirmModal,
        },

        props: {
            user: {
                type: User,
            },
        },

        setup(props, context) {
            const store = useStore()

            const state = reactive({
                overlay: true,
                meta: undefined,
            })

            const queries = reactive({
                page: 1,
                per_page: 20,
                sort: '-id',
                search: <string | undefined>undefined,
                statuses: <string>'' ,
                type: <string>'',
            })

            const collection = computed(() => props.user
                ? <UserMainGoalCollection>store.getters['userMainGoalCollection/get']
                : <MainGoalCollection>store.getters['mainGoalCollection/get']
            )

            const { mainGoalStatuses, mainGoalTypes } = useEnums()

            const statusItems = computed(() => [{ text: '-', value: '' }, ...mainGoalStatuses()])
            const typeItems = computed(() => [{ text: '-', value: '' }, ...mainGoalTypes()])

            const capture = () => {
                state.overlay = true
                const dispatch = props.user
                    ? store.dispatch('userMainGoalCollection/get', { userId: props.user.id, params: queries })
                    : store.dispatch('mainGoalCollection/get', { ...queries })

                dispatch.then((data: any) => {
                    state.meta = data.meta
                    state.overlay = false
                })
            }

            const updated = (updatedQueries: any) => {
                queries.page = updatedQueries.page
                capture()
            }

            const touch = (goal: UserMainGoal) => {
                context.emit('touched', goal)
            }

            const del = (goal: UserMainGoal) => {
                store.dispatch('userMainGoalCollection/delete', {
                    userId: props.user!.id,
                    id: goal.id,
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: 'Deleted' })
                })
            }

            const statusColor = (status: string): string => {
                const map: Record<string, string> = {
                    draft: 'grey',
                    in_progress: 'primary',
                    finished: 'success',
                    abandoned: 'orange darken-2',
                }
                return map[status] || 'grey'
            }

            const clearSearch = () => {
                queries.search = undefined
                capture()
            }

            const reset = () => {
                queries.search = undefined
                queries.statuses = ''
                queries.type = ''
                queries.page = 1
                capture()
            }

            onMounted(() => {
                collection.value.data = []
                capture()
            })

            return {
                collection: collection.value,
                queries,
                statusItems,
                typeItems,
                updated,
                touch,
                del,
                statusColor,
                clearSearch,
                reset,
                capture,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state),
            }
        }
    })
</script>
